// src/layout/sideBar.js
import React, { useState } from "react";
import "./sideBar.css";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ArrowUpRight, BarChart3, Sparkles } from "lucide-react";
import {
    fetchCommercialUsage,
    openCommercialGateModal,
} from "../../../Actions/commercialControl.action";
import { usePageTransition } from "../../../utils/pageTransition";
import { buildLockedCommercialDecision } from "../../../utils/commercialCapabilities";
import UsagePanel from "../../stage-review/commercial/UsagePanel";

/**
 * Props:
 * - currentRole: string | null
 * - headerItems: Array<{ nameKey?: string; name?: string; link: string; icon?: ReactNode; roles?: string[]; hidden?: boolean; groupKey?: string; }>
 * - isCollapsed: boolean
 * - isMobileOpen: boolean
 * - onNavigate: () => void
 */
export default function SideBar({ currentRole, headerItems = [], isCollapsed, isMobileOpen = false, onNavigate }) {
    const location = useLocation();
    const { t } = useTranslation("navigation");
    const dispatch = useDispatch();
    const cinematicActive = useSelector((state) => !!state.ui?.cinematicUpload?.active);
    const commercialControl = useSelector((state) => state.commercialControl || {});
    const { navigateWithFade } = usePageTransition();
    const [usagePanelOpen, setUsagePanelOpen] = useState(false);
    const isSuperAdmin = currentRole === "SUPER_ADMIN";
    const shouldShowExpandedContent = !isCollapsed || isMobileOpen;
    const canManageBilling = ["SUPER_ADMIN", "ADMIN_CLIENT"].includes(currentRole);
    const billingReturnPath = currentRole === "SUPER_ADMIN"
        ? "/admin/commercial?tab=subscriptions"
        : "/workspace";

    const visibleItems = headerItems.filter((item) => {
        const roleOk = Array.isArray(item.roles) ? item.roles.includes(currentRole) : true;
        return roleOk && !item.hidden;
    });
    const groupedItems = visibleItems.reduce((groups, item) => {
        const groupKey = item.groupKey || "work";
        const group = groups.find((entry) => entry.key === groupKey);

        if (group) {
            group.items.push(item);
            return groups;
        }

        return [...groups, { key: groupKey, items: [item] }];
    }, []);
    const shouldShowGroups = !isCollapsed && visibleItems.length >= 4 && groupedItems.length > 1;
    const usageByType = commercialControl.usageByType || commercialControl.usageSnapshot?.usage || {};
    const capabilities = commercialControl.capabilities || commercialControl.usageSnapshot?.capabilities || {};
    const plan = commercialControl.plan || commercialControl.usageSnapshot?.plan || null;
    const planCode = isSuperAdmin
        ? t("subscription.superAdminPlan", { defaultValue: "Super admin" })
        : String(plan?.name || plan?.code || t("subscription.unknownPlan", { defaultValue: "Plan actif" }));
    const hasPlanContext = !!plan || Object.keys(usageByType).length > 0;
    const displayStatusLabel = isSuperAdmin
        ? t("subscription.platformAccess", { defaultValue: "Accès plateforme" })
        : hasPlanContext
        ? t("subscription.activeAccess", { defaultValue: "Accès actif" })
        : t("subscription.planLoading", { defaultValue: "Plan à charger" });
    const displayUsageLabel = isSuperAdmin
        ? t("subscription.platformScope", { defaultValue: "Console interne" })
        : t("subscription.currentPlan", { defaultValue: "Plan en cours" });

    const refreshCommercialUsage = () => {
        const result = dispatch(fetchCommercialUsage());
        if (result?.catch) result.catch(() => {});
        return result;
    };

    const openUsagePanel = () => {
        setUsagePanelOpen(true);
        refreshCommercialUsage();
    };

    const openUsageUpgradeOptions = () => {
        dispatch(openCommercialGateModal(buildLockedCommercialDecision("complete_analysis", {
            code: "usage_panel_upgrade_prompt",
            titleFr: "Changer de plan",
            bodyFr: "Choisissez une offre supérieure pour augmenter les capacités disponibles.",
            titleEn: "Change plan",
            bodyEn: "Choose a higher plan to increase available capabilities.",
            ctaFr: "Voir les options",
            ctaEn: "View options",
        })));
    };

    const renderItem = (item) => {
        const isActive = location.pathname.startsWith(item.link);
        const isStageReviewItem = item.link === "/stage-review";
        const cinematicClass = cinematicActive && isStageReviewItem ? "sidebar-link-cinematic" : "";
        const label = item.nameKey ? t(item.nameKey) : (item.name || "");

        return (
            <li key={item.link} className={`sidebar-item ${isActive ? "active" : ""}`}>
                <button
                    type="button"
                    className={`sidebar-link ${isActive ? "active" : ""} ${cinematicClass}`.trim()}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => {
                        onNavigate?.();
                        navigateWithFade(item.link);
                    }}
                >
                    {item.icon ? <span className="sidebar-icon">{item.icon}</span> : null}
                    {shouldShowExpandedContent && <span className="sidebar-text">{label}</span>}
                </button>
            </li>
        );
    };

    return (
        <>
            <aside
                id="app-sidebar"
                className={`sidebar ${isCollapsed ? "collapsed" : ""} ${isMobileOpen ? "mobile-open" : ""}`.trim()}
            >
                <div className="sidebar-content">
                    <ul className={`sidebar-list ${shouldShowGroups ? "sidebar-list-grouped" : ""}`}>
                        {shouldShowGroups
                            ? groupedItems.map((group) => (
                                <li key={group.key} className="sidebar-section">
                                    {group.items.length > 1 && (
                                        <div className="sidebar-section-title">{t(`groups.${group.key}`)}</div>
                                    )}
                                    <ul className="sidebar-section-list">
                                        {group.items.map(renderItem)}
                                    </ul>
                                </li>
                            ))
                            : visibleItems.map(renderItem)}
                    </ul>
                    {shouldShowExpandedContent && (
                        <section className="sidebar-subscription" aria-label={t("subscription.ariaLabel", { defaultValue: "Abonnement et usage" })}>
                            <div className="sidebar-subscription-top">
                                <span className="sidebar-subscription-eyebrow">
                                    <Sparkles size={13} />
                                    {t("subscription.eyebrow", { defaultValue: "Abonnement" })}
                                </span>
                                <span className="sidebar-subscription-plan">{planCode}</span>
                            </div>
                            <div className="sidebar-subscription-main">
                                <span className="sidebar-subscription-value">{displayStatusLabel}</span>
                                <span className="sidebar-subscription-label">
                                    {displayUsageLabel}
                                </span>
                            </div>
                            {!isSuperAdmin && (
                                <button type="button" className="sidebar-subscription-cta" onClick={openUsagePanel}>
                                    <BarChart3 size={15} />
                                    {t("subscription.cta", { defaultValue: "Plan et usage" })}
                                    <ArrowUpRight size={14} />
                                </button>
                            )}
                        </section>
                    )}
                </div>
                <div className="sidebar-grid-fade" aria-hidden="true" />
            </aside>
            <UsagePanel
                open={usagePanelOpen}
                onClose={() => setUsagePanelOpen(false)}
                usageSnapshot={commercialControl.usageSnapshot}
                usageByType={usageByType}
                capabilities={capabilities}
                plan={plan}
                isLoading={commercialControl.isLoadingUsage}
                error={commercialControl.lastCommercialError}
                onRefresh={refreshCommercialUsage}
                onUpgrade={openUsageUpgradeOptions}
                canManageBilling={canManageBilling}
                billingReturnPath={billingReturnPath}
            />
        </>
    );
}
